import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * View store for the AI → LLM Usage admin view ("AI Delivery Observability &
 * Cost Control").
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The read-only `llm-usage-summary` data is read through the
 * registered `llmUsageSummaryStore` ServiceStore (see serviceStores/ai.stores.ts):
 * `find()` populates that store's entity cache and `rows` is a computed view
 * over it (per the MobX rule, API entities live in the ServiceStore, never in a
 * ViewStore).
 *
 * Each summary row is grouped by (providerType, taskKey) and carries the call
 * count, token totals, total cost, average latency, and error rate. This view
 * surfaces those aggregates so admins can watch LLM spend, throughput, and error
 * rates per provider/task at a glance.
 */

/** One usage-summary row as the API serialises it (grouped by providerType + taskKey). */
export interface LlmUsageSummaryRow {
  providerType: string | null;
  taskKey: string;
  /** Friendly task label from the registry (falls back to the raw key). */
  taskLabel?: string;
  callCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  avgLatencyMs: number | null;
  errorCount: number;
  /** Fraction in [0, 1], 3dp (e.g. 0.125 = 12.5%). */
  errorRate: number;
}

/** A per-task rollup, aggregated across providers (the "By Task" view). */
export interface LlmUsageByTaskRow {
  taskKey: string;
  taskLabel: string;
  callCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  errorCount: number;
  /** Fraction in [0, 1] computed from the summed counts. */
  errorRate: number;
}

/** Filter value for the provider-type header control ("all" or a provider type). */
export type LlmUsageProviderTypeFilter = "all" | string;

/** Sort keys for the "By Task" rollup table. */
export type LlmUsageByTaskSortKey =
  | "task"
  | "callCount"
  | "tokens"
  | "cost"
  | "errorRate";

/** Sort keys for the "By Provider & Task" table. */
export type LlmUsageProviderSortKey =
  | "provider"
  | "task"
  | "callCount"
  | "inputTokens"
  | "outputTokens"
  | "cost"
  | "avgLatency"
  | "errorRate";

/** Numeric telemetry columns read best biggest-first on first click. */
const BY_TASK_DESC_FIRST: ReadonlySet<LlmUsageByTaskSortKey> = new Set([
  "callCount",
  "tokens",
  "cost",
  "errorRate",
]);

const PROVIDER_DESC_FIRST: ReadonlySet<LlmUsageProviderSortKey> = new Set([
  "callCount",
  "inputTokens",
  "outputTokens",
  "cost",
  "avgLatency",
  "errorRate",
]);

/** Sentinel label used for rows whose providerType is null/blank. */
export const LLM_USAGE_UNKNOWN_PROVIDER = "unknown";

/** Human label for a (possibly null) provider type. */
export const llmUsageProviderLabel = (providerType?: string | null): string =>
  providerType && providerType.trim() ? providerType : LLM_USAGE_UNKNOWN_PROVIDER;

class LlmUsageStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  providerTypeFilter: LlmUsageProviderTypeFilter = "all";

  // Column sorts. The two tables carry different column sets (a combined
  // "Tokens" rollup vs input/output tokens + latency), so each keeps its own
  // independent sort state rather than sharing one. Both default to cost
  // descending — this is a cost table, biggest spend on top.
  byTaskSortKey: LlmUsageByTaskSortKey = "cost";
  byTaskSortDir: SortDir = "desc";
  providerSortKey: LlmUsageProviderSortKey = "cost";
  providerSortDir: SortDir = "desc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `llm-usage-summary` ServiceStore (see serviceStores/ai.stores.ts). */
  private get service(): ServiceStoreType | null {
    return (this.rootStore.llmUsageSummaryStore as ServiceStoreType | null) ?? null;
  }

  /**
   * Summary rows. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get rows(): LlmUsageSummaryRow[] {
    return (this.service?.entities ?? []) as unknown as LlmUsageSummaryRow[];
  }

  // ---- Derived data -------------------------------------------------------

  /** Distinct provider types present in the data (for the header filter). */
  get providerTypes(): string[] {
    const seen = new Set<string>();
    for (const r of this.rows) {
      seen.add(llmUsageProviderLabel(r.providerType));
    }
    return Array.from(seen).sort();
  }

  get filteredRows(): LlmUsageSummaryRow[] {
    let result = this.rows;

    if (this.providerTypeFilter !== "all") {
      result = result.filter(
        (r) => llmUsageProviderLabel(r.providerType) === this.providerTypeFilter
      );
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.taskKey?.toLowerCase().includes(q) ||
          llmUsageProviderLabel(r.providerType).toLowerCase().includes(q)
      );
    }

    return result;
  }

  /** True when a search or provider filter is narrowing the rows. */
  get hasActiveFilters(): boolean {
    return this.providerTypeFilter !== "all" || this.searchQuery.trim() !== "";
  }

  get rowCount(): number {
    return this.rows.length;
  }

  /**
   * Per-task rollup: the same telemetry aggregated across providers, so admins
   * can see cost/calls/errors by analysis task (action items vs summary vs
   * happiness, …). Sorted by cost descending. Respects the active filters.
   */
  get byTask(): LlmUsageByTaskRow[] {
    const map = new Map<string, LlmUsageByTaskRow>();
    for (const r of this.filteredRows) {
      let row = map.get(r.taskKey);
      if (!row) {
        row = {
          taskKey: r.taskKey,
          taskLabel: r.taskLabel || r.taskKey,
          callCount: 0,
          totalInputTokens: 0,
          totalOutputTokens: 0,
          totalCostUsd: 0,
          errorCount: 0,
          errorRate: 0,
        };
        map.set(r.taskKey, row);
      }
      row.callCount += r.callCount || 0;
      row.totalInputTokens += r.totalInputTokens || 0;
      row.totalOutputTokens += r.totalOutputTokens || 0;
      row.totalCostUsd += r.totalCostUsd || 0;
      row.errorCount += r.errorCount || 0;
    }
    const rows = Array.from(map.values());
    for (const row of rows) {
      row.errorRate = row.callCount > 0 ? row.errorCount / row.callCount : 0;
    }
    return rows.sort((a, b) => b.totalCostUsd - a.totalCostUsd);
  }

  /** The "By Task" rows under the active column sort. */
  get sortedByTask(): LlmUsageByTaskRow[] {
    const key = this.byTaskSortKey;
    const valueOf = (t: LlmUsageByTaskRow): SortValue => {
      switch (key) {
        case "task":
          return textValue(t.taskLabel || t.taskKey);
        case "callCount":
          return t.callCount || 0;
        case "tokens":
          return (t.totalInputTokens || 0) + (t.totalOutputTokens || 0);
        case "cost":
          return t.totalCostUsd || 0;
        case "errorRate":
          return t.errorRate || 0;
      }
    };
    return sortRows(this.byTask, valueOf, this.byTaskSortDir);
  }

  /**
   * The "By Provider & Task" rows: filters, then the active column sort.
   * Unknown latency (null) sorts last in either direction.
   */
  get sortedProviderRows(): LlmUsageSummaryRow[] {
    const key = this.providerSortKey;
    const valueOf = (r: LlmUsageSummaryRow): SortValue => {
      switch (key) {
        case "provider":
          return textValue(llmUsageProviderLabel(r.providerType));
        case "task":
          return textValue(r.taskLabel || r.taskKey);
        case "callCount":
          return r.callCount || 0;
        case "inputTokens":
          return r.totalInputTokens || 0;
        case "outputTokens":
          return r.totalOutputTokens || 0;
        case "cost":
          return r.totalCostUsd || 0;
        case "avgLatency":
          return r.avgLatencyMs ?? null;
        case "errorRate":
          return r.errorRate || 0;
      }
    };
    return sortRows(this.filteredRows, valueOf, this.providerSortDir);
  }

  // The headline cards summarise exactly what the tables below them show, so
  // every total is computed over `filteredRows`, NOT `rows`. Summing the
  // unfiltered set made the cards disagree with the table the moment a search
  // or provider filter was applied — a cost figure that doesn't match the rows
  // it sits above is worse than no figure at all.

  /** Sum of `totalCostUsd` across the filtered rows. */
  get totalCost(): number {
    return this.filteredRows.reduce((sum, r) => sum + (r.totalCostUsd || 0), 0);
  }

  /** Sum of `callCount` across the filtered rows. */
  get totalCalls(): number {
    return this.filteredRows.reduce((sum, r) => sum + (r.callCount || 0), 0);
  }

  /** Sum of `errorCount` across the filtered rows. */
  get totalErrors(): number {
    return this.filteredRows.reduce((sum, r) => sum + (r.errorCount || 0), 0);
  }

  /** Total tokens (input + output) across the filtered rows. */
  get totalTokens(): number {
    return this.filteredRows.reduce(
      (sum, r) => sum + (r.totalInputTokens || 0) + (r.totalOutputTokens || 0),
      0
    );
  }

  /**
   * Overall error rate as a fraction in [0, 1]: total errors / total calls,
   * over the FILTERED rows. Computed from the raw counts (not an average of
   * per-row rates) so it is not skewed by low-volume groups. Returns 0 when
   * there are no calls.
   */
  get overallErrorRate(): number {
    const calls = this.totalCalls;
    if (calls <= 0) return 0;
    return this.totalErrors / calls;
  }

  /**
   * The "By Provider & Task" table flattened to plain rows for CSV export.
   * Reads `sortedProviderRows`, so the file carries exactly the rows (and the
   * order) the operator is looking at — filters included. The error rate is
   * emitted as a percentage because the CSV column is formatted as one.
   */
  get exportRows() {
    return this.sortedProviderRows.map((r) => ({
      provider: llmUsageProviderLabel(r.providerType),
      task: r.taskLabel || r.taskKey,
      taskKey: r.taskKey,
      callCount: r.callCount ?? 0,
      inputTokens: r.totalInputTokens ?? 0,
      outputTokens: r.totalOutputTokens ?? 0,
      costUsd: r.totalCostUsd ?? 0,
      avgLatencyMs: r.avgLatencyMs ?? null,
      errorCount: r.errorCount ?? 0,
      errorRatePercent: (r.errorRate ?? 0) * 100,
    }));
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setProviderTypeFilter(filter: LlmUsageProviderTypeFilter) {
    this.providerTypeFilter = filter;
  }

  /** "By Task" header click: same column toggles, a new column starts in its
   *  natural direction (numbers biggest-first, task text A→Z). */
  setByTaskSort(key: LlmUsageByTaskSortKey) {
    const next = nextSort(
      this.byTaskSortKey,
      this.byTaskSortDir,
      key,
      BY_TASK_DESC_FIRST
    );
    this.byTaskSortKey = next.key;
    this.byTaskSortDir = next.dir;
  }

  /** "By Provider & Task" header click: same semantics, independent state. */
  setProviderSort(key: LlmUsageProviderSortKey) {
    const next = nextSort(
      this.providerSortKey,
      this.providerSortDir,
      key,
      PROVIDER_DESC_FIRST
    );
    this.providerSortKey = next.key;
    this.providerSortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `rows` reads.
      //
      // The query is deliberately EMPTY, and that is load-bearing twice over:
      //
      // 1. `$limit` is not merely ignored here — it is a 400. The service is a
      //    hand-written find-only rollup whose query schema
      //    (llm-usage-summary.schema.ts) declares ONLY `taskKey` and
      //    `providerType` under `additionalProperties: false`, and the
      //    validator runs without `removeAdditional`. Sending `$limit: 200`
      //    (as this call did until 2026-08-16) failed validation outright, so
      //    the view rendered "validation failed" and zero rows rather than a
      //    truncated total. Verified against the running API: with `$limit`,
      //    HTTP 400 `must NOT have additional properties`; without it, 200.
      //
      // 2. Because there is no `$limit`, there is also no truncation to be
      //    honest about. The class applies no `.limit()`/`.offset()` at all —
      //    it GROUPs `llm_call_logs` by (providerType, taskKey) and returns
      //    every group, and that cardinality is bounded by the 5 provider types
      //    x 13 registered tasks (~78 rows ceiling, a handful in practice).
      //    So summing `totalCostUsd` client-side over these rows is a
      //    mathematically COMPLETE grand total, not a sample — which is what
      //    makes the Total Cost card safe to display as a plain figure.
      //
      // Note the response's own `total` is just `data.length` (a count of
      // rollup rows); it is not a cost aggregate and must not be read as one.
      // There is no server-side cost-aggregate endpoint anywhere in the API.
      //
      // `findOrThrow`, not a bare `find`: the ServiceStore swallows a failed
      // find (resolves null, records errorOnFind), which left this catch dead
      // and the page showing $0.00 · 0 calls · a green 0.0% error rate over
      // "No LLM usage data available" while the summary had in fact failed.
      await findOrThrow(this.service, { query: {} });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load LLM usage");
      });

      console.error("Failed to load LLM usage summary:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.load();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.providerTypeFilter = "all";
    this.byTaskSortKey = "cost";
    this.byTaskSortDir = "desc";
    this.providerSortKey = "cost";
    this.providerSortDir = "desc";
  }
}

export default LlmUsageStore;
